// 学科深页: 4 个 level (L1-L4) 的 cell 展示 + 所有 problems
import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  CURRICULUM, BRANCH_ORDER, LEVEL_ORDER, getCellProblems, getCellRepresentative, getBranchProblems
} from '../lib/curriculum.js';
import { useI18n, t } from '../lib/i18n.js';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

const LEVEL_INFO = {
  1: { name_zh: '火花', name_en: 'Spark',    icon: '🔥', color: 'from-rose-500/20 to-pink-500/10 border-rose-500/30' },
  2: { name_zh: '探究', name_en: 'Explorer', icon: '🔍', color: 'from-amber-500/20 to-orange-500/10 border-amber-500/30' },
  3: { name_zh: '实践', name_en: 'Solver',   icon: '⚙️', color: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30' },
  4: { name_zh: '创造', name_en: 'Creator',  icon: '🌟', color: 'from-violet-500/20 to-fuchsia-500/10 border-violet-500/30' },
};

const DIFFICULTY_LABEL = { 1: '入门', 2: '简单', 3: '中等', 4: '困难', 5: '极难' };
const STATUS_LABEL = { open: '未解', partially_solved: '部分解', solved: '已解' };
const STATUS_COLOR = { open: 'bg-amber-500/20 text-amber-300', partially_solved: 'bg-cyan-500/20 text-cyan-300', solved: 'bg-emerald-500/20 text-emerald-300' };

export default function BranchDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const branchId = id.toUpperCase();
  const branch = CURRICULUM.branches[branchId];
  const { lang } = useI18n();
  const isZh = lang === 'zh-CN';
  const initialLevel = parseInt(params.get('level') || '2');
  const [activeLevel, setActiveLevel] = useState(initialLevel);

  useDocumentTitle(branch ? (isZh ? branch.name_zh : branch.name_en) + ' · Academy' : 'Academy');

  useEffect(() => {
    setActiveLevel(initialLevel);
  }, [initialLevel, branchId]);

  if (!branch) {
    return (
      <div className="container-page py-12 text-center">
        <div className="text-4xl mb-3">🤷</div>
        <h2 className="text-2xl font-bold mb-2">{isZh ? '学科不存在' : 'Branch not found'}</h2>
        <Link to="/academy" className="text-violet-400 hover:underline">← {isZh ? '返回学院' : 'Back to Academy'}</Link>
      </div>
    );
  }

  const allProblems = getBranchProblems(branchId);
  const activeProblems = useMemo(() => getCellProblems(`${branchId}.L${activeLevel}`), [branchId, activeLevel]);
  const representative = useMemo(() => getCellRepresentative(`${branchId}.L${activeLevel}`), [branchId, activeLevel]);

  return (
    <div className="container-page py-8 max-w-6xl mx-auto">
      {/* Hero */}
      <div className="mb-6">
        <Link to="/academy" className="text-sm text-slate-500 hover:text-slate-300 mb-2 inline-block">
          ← {isZh ? '学院' : 'Academy'}
        </Link>
        <div className="card bg-gradient-to-br border-white/10" style={{ background: `linear-gradient(135deg, var(--tw-gradient-stops))` }}>
          <div className="flex items-start gap-4">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${branch.color} flex items-center justify-center text-4xl flex-shrink-0`}>
              {branch.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl sm:text-4xl font-display font-extrabold mb-2">
                {isZh ? branch.name_zh : branch.name_en}
              </h1>
              <p className="text-slate-300 mb-3">
                {isZh ? branch.desc_zh : branch.desc_en}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span>📚 <b className="text-slate-100">{allProblems.length}</b> {isZh ? '题' : 'problems'}</span>
                <span>🪜 4 {isZh ? '阶梯' : 'levels'}</span>
                <Link to="/academy/tree" className="text-cyan-400 hover:underline">
                  🌳 {isZh ? '在知识树中查看' : 'View in tree'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4 阶梯 tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {LEVEL_ORDER.map(lv => {
          const info = LEVEL_INFO[lv];
          const count = CURRICULUM.cells[`${branchId}.L${lv}`]?.count || 0;
          const active = activeLevel === lv;
          return (
            <button
              key={lv}
              onClick={() => {
                setActiveLevel(lv);
                setParams({ level: lv });
              }}
              className={`p-3 rounded-lg border transition-all text-left ${
                active
                  ? `bg-gradient-to-br ${info.color} shadow-lg scale-[1.02]`
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-2xl">{info.icon}</div>
                <div className="text-xs font-mono text-slate-500">L{lv}</div>
              </div>
              <div className="text-sm font-bold">{isZh ? info.name_zh : info.name_en}</div>
              <div className="text-xs text-slate-400 mt-1">{count} {isZh ? '题' : 'qs'}</div>
            </button>
          );
        })}
      </div>

      {/* 当前 level 说明 */}
      <div className="card mb-6 bg-violet-500/5 border-violet-500/20">
        <div className="flex items-center gap-3 mb-2">
          <div className="text-3xl">{LEVEL_INFO[activeLevel].icon}</div>
          <div>
            <div className="text-xs text-slate-500 font-mono">L{activeLevel}</div>
            <div className="text-xl font-bold">{isZh ? LEVEL_INFO[activeLevel].name_zh : LEVEL_INFO[activeLevel].name_en}</div>
          </div>
        </div>
        <p className="text-sm text-slate-400">
          {activeLevel === 1 && (isZh ? 'L1 火花:用故事和直觉接触这道学科。问问题比答问题重要。' : 'L1 Spark: use stories and intuition to encounter the field. Asking > answering.')}
          {activeLevel === 2 && (isZh ? 'L2 探究:用 1-4 周调研,理解基础工具 (公式/方法/历史)。' : 'L2 Explorer: spend 1-4 weeks on basics (formulas, methods, history).')}
          {activeLevel === 3 && (isZh ? 'L3 实践:用所学工具解决一个真实子问题,做原型/论文/代码。' : 'L3 Solver: apply your tools to a real sub-problem, ship a prototype/paper/code.')}
          {activeLevel === 4 && (isZh ? 'L4 创造:提出新问题,主导团队,产出可被引用的成果。' : 'L4 Creator: pose new questions, lead a team, ship citable work.')}
        </p>
      </div>

      {/* 代表题 (精选 5 道) */}
      {representative.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-3">⭐ {isZh ? '代表题' : 'Representative problems'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {representative.map(p => (
              <Link
                key={p.id}
                to={`/problems/${p.id}`}
                className="card hover:border-violet-500/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-sm flex-1 line-clamp-2">
                    {isZh ? p.title : p.titleEn}
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded font-mono ${STATUS_COLOR[p.status]}`}>
                    {STATUS_LABEL[p.status]}
                  </span>
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 mb-2">{isZh ? p.summary : p.summaryEn}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>⚡ {p.reward || 100}</span>
                  <span>· {DIFFICULTY_LABEL[p.difficulty]}</span>
                  <span>· {p.year}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 全部题 */}
      <div>
        <h2 className="text-lg font-bold mb-3">📋 {isZh ? `全部 L${activeLevel} 题 (${activeProblems.length})` : `All L${activeLevel} problems (${activeProblems.length})`}</h2>
        {activeProblems.length === 0 ? (
          <div className="card text-center text-slate-500 py-12">
            <div className="text-3xl mb-2">🌱</div>
            {isZh ? '这个 cell 暂时没题' : 'No problems in this cell yet'}
          </div>
        ) : (
          <ul className="space-y-2">
            {activeProblems.map(p => (
              <li key={p.id}>
                <Link
                  to={`/problems/${p.id}`}
                  className="card hover:border-violet-500/40 transition-colors flex items-center gap-3"
                >
                  <div className={`w-1 self-stretch rounded ${LEVEL_INFO[activeLevel].color.split(' ')[1]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold truncate">{isZh ? p.title : p.titleEn}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${STATUS_COLOR[p.status]}`}>
                        {STATUS_LABEL[p.status]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-1">{isZh ? p.summary : p.summaryEn}</p>
                  </div>
                  <div className="flex flex-col items-end text-xs text-slate-500 gap-0.5">
                    <span>⚡ {p.reward || 100}</span>
                    <span className="font-mono">d={p.difficulty}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
